import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../components/ui/button';
import PermButton from '@/components/PermButton';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Download, Search, Filter, X,
  CheckCircle, AlertCircle, Loader2, ChevronLeft, ChevronRight, Building2, AlertTriangle,
  LayoutGrid, Table as TableIcon,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import LogDamageDialog from '@/components/LogDamageDialog';
import { AgingReportPanel } from '@/components/AgingReportPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 30;
const DEFAULT_BRANCH_NAME = 'Unit 1 Vedica';

// Donut palette — kept colour-blind safe by ordering "Others" → muted grey.
const DONUT_COLOURS = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#ec4899', // pink
  '#14b8a6', // teal
  '#a855f7', // purple
  '#64748b', // slate ("Others")
];

const fmtInt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtINR = (n) => `₹ ${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtINRCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹ ${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹ ${(v / 1e5).toFixed(2)} L`;
  return fmtINR(v);
};

export default function Inventory() {
  const [loading, setLoading] = useState(false);
  const [filtersData, setFiltersData] = useState({ branches: [], models: [], verticals: [], brands: [] });

  const [fgInventory, setFgInventory] = useState([]);
  const [fgTotal, setFgTotal] = useState(0);
  const [fgFilteredTotals, setFgFilteredTotals] = useState(null);
  const [fgFilters, setFgFilters] = useState({ branch_id: '', vertical_id: '', model_id: '', brand_ids: [], search: '' });
  const [preselectDone, setPreselectDone] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Top-level view toggle.
  const [view, setView] = useState('summary'); // 'summary' | 'detailed'

  // Summary payload.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Import dialog
  const [importDialog, setImportDialog] = useState(false);
  const [importMode, setImportMode] = useState('add');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Damage logging
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);

  // Feb 2026 — Phase 2 migration (defect A + B). Debounce the free-text
  // search so we don't hammer the backend, and use monotonic reqId
  // counters so an older list/summary response cannot overwrite a
  // newer one.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(fgFilters.search), 250);
    return () => clearTimeout(t);
  }, [fgFilters.search]);
  const fgListReqIdRef = useRef(0);
  const fgSummaryReqIdRef = useRef(0);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/inventory/filters`, { headers: getHeaders() });
      setFiltersData(response.data);
    } catch (e) {
      console.error('Error fetching filters:', e);
    }
  }, []);

  // Preselect default branch = Vedica once filters are loaded (one-time).
  useEffect(() => {
    if (preselectDone || !filtersData.branches?.length) return;
    const vedica = filtersData.branches.find((b) => b.name === DEFAULT_BRANCH_NAME);
    if (vedica) {
      setFgFilters((f) => ({ ...f, branch_id: vedica.branch_id }));
    }
    setPreselectDone(true);
  }, [filtersData.branches, preselectDone]);

  // Build URLSearchParams from current filter state (shared by both views).
  const buildFilterParams = useCallback(() => {
    const p = new URLSearchParams();
    if (fgFilters.branch_id) p.append('branch_id', fgFilters.branch_id);
    if (fgFilters.vertical_id) p.append('vertical_id', fgFilters.vertical_id);
    if (fgFilters.model_id) p.append('model_id', fgFilters.model_id);
    fgFilters.brand_ids.forEach((id) => p.append('brand_id', id));
    if (debouncedSearch) p.append('search', debouncedSearch);
    return p;
  }, [fgFilters.branch_id, fgFilters.vertical_id, fgFilters.model_id, fgFilters.brand_ids, debouncedSearch]);

  // --- Detailed view fetch (existing /fg list endpoint) ---
  const fetchFgInventory = useCallback(async () => {
    if (!preselectDone) return;
    const myReqId = ++fgListReqIdRef.current;
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.append('skip', String((page - 1) * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      const response = await axios.get(`${API}/api/inventory/fg?${params}`, { headers: getHeaders() });
      if (myReqId !== fgListReqIdRef.current) return;
      setFgInventory(response.data.items || []);
      setFgTotal(response.data.total || 0);
      setFgFilteredTotals(response.data.filtered_totals || null);
    } catch (e) {
      if (myReqId !== fgListReqIdRef.current) return;
      toast.error('Failed to fetch FG inventory');
      setFgFilteredTotals(null);
    }
    if (myReqId === fgListReqIdRef.current) setLoading(false);
  }, [buildFilterParams, page, preselectDone]);

  // --- Summary view fetch ---
  const fetchSummary = useCallback(async () => {
    if (!preselectDone) return;
    const myReqId = ++fgSummaryReqIdRef.current;
    setSummaryLoading(true);
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/api/inventory/fg/summary?${params}`, { headers: getHeaders() });
      if (myReqId !== fgSummaryReqIdRef.current) return;
      setSummary(response.data);
    } catch (e) {
      if (myReqId !== fgSummaryReqIdRef.current) return;
      toast.error('Failed to fetch FG summary');
    }
    if (myReqId === fgSummaryReqIdRef.current) setSummaryLoading(false);
  }, [buildFilterParams, preselectDone]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [fgFilters.branch_id, fgFilters.vertical_id, fgFilters.model_id, fgFilters.brand_ids, debouncedSearch]);

  // Fetch detailed list when in detailed view OR for export bookkeeping.
  useEffect(() => {
    if (view === 'detailed') fetchFgInventory();
  }, [view, fetchFgInventory]);

  // Fetch summary only when summary view is active.
  useEffect(() => {
    if (view === 'summary') fetchSummary();
  }, [view, fetchSummary]);

  const filteredModels = fgFilters.vertical_id
    ? (filtersData.models || []).filter((m) => m.vertical_id === fgFilters.vertical_id)
    : (filtersData.models || []);

  const totalPages = Math.max(1, Math.ceil(fgTotal / PAGE_SIZE));
  const startIdx = fgTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, fgTotal);

  const activeFilterCount = useMemo(
    () => ['vertical_id', 'model_id'].filter((k) => fgFilters[k]).length + (fgFilters.brand_ids.length ? 1 : 0),
    [fgFilters]
  );

  // Aging report panel reuses the page branch filter as-is (branch_id).
  const fgAgingParams = useMemo(
    () => (fgFilters.branch_id ? { branch_id: fgFilters.branch_id } : {}),
    [fgFilters.branch_id],
  );
  const selectedBranchName = useMemo(() => {
    if (!fgFilters.branch_id) return 'All Branches';
    const b = (filtersData.branches || []).find((br) => br.branch_id === fgFilters.branch_id);
    return b?.name || '';
  }, [filtersData.branches, fgFilters.branch_id]);
  const selectedBrandNames = useMemo(
    () => (filtersData.brands || []).filter((b) => fgFilters.brand_ids.includes(b.id)).map((b) => b.name),
    [filtersData.brands, fgFilters.brand_ids],
  );
  const brandFilterLabel = fgFilters.brand_ids.length === 0
    ? 'All Brands'
    : fgFilters.brand_ids.length === 1
      ? (selectedBrandNames[0] || '1 brand')
      : `${fgFilters.brand_ids.length} brands`;
  const toggleBrand = (brandId) => {
    setFgFilters((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(brandId)
        ? f.brand_ids.filter((id) => id !== brandId)
        : [...f.brand_ids, brandId],
    }));
  };

  const clearAdvancedFilters = () => {
    setFgFilters((f) => ({ ...f, vertical_id: '', model_id: '', brand_ids: [] }));
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/inventory/fg/template`, {
        headers: getHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'fg_inventory_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  // Detailed view export — now honours ALL active filters.
  const handleExportDetailed = async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/api/inventory/fg/export?${params}`, {
        headers: getHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const branch = (selectedBranchName || 'all').replace(/\s+/g, '_');
      link.setAttribute('download', `fg_inventory_${branch}_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported ${fgTotal} row${fgTotal === 1 ? '' : 's'} for current filters`);
    } catch (e) {
      toast.error('Failed to export FG inventory');
    }
  };

  // Summary export — multi-sheet.
  const handleExportSummary = async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/api/inventory/fg/summary/export?${params}`, {
        headers: getHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const branch = (selectedBranchName || 'all').replace(/\s+/g, '_');
      link.setAttribute('download', `fg_summary_${branch}_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Summary exported (4 sheets)');
    } catch (e) {
      toast.error('Failed to export summary');
    }
  };


  const downloadFgSnapshot = async (label) => {
    try {
      const params = new URLSearchParams();
      if (fgFilters.branch_id) params.append('branch_id', fgFilters.branch_id);
      const response = await axios.get(`${API}/api/inventory/fg/export?${params}`, {
        headers: getHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const branch = (selectedBranchName || 'all').replace(/\s+/g, '_');
      link.setAttribute('download', `fg_stock_PRE_${label}_${branch}_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImporting(true);
    setImportResult(null);

    if (importMode === 'replace') {
      const snapshotOk = await downloadFgSnapshot('REPLACE');
      if (!snapshotOk) {
        toast.error("Couldn't download the pre-import snapshot. Aborting Replace to protect your data.", { duration: 8000 });
        setImporting(false);
        return;
      }
      toast.info("Pre-import snapshot downloaded.", { duration: 6000 });
    }

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { uploadWithReceipt } = await import('../lib/bulkUploadReceipt');
      const data = await uploadWithReceipt(
        `${API}/api/inventory/fg/bulk-import?mode=${importMode}`,
        formData,
        { axiosConfig: { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }, showToast: false }
      );
      setImportResult(data);
      if (data.success) {
        toast.success(`Import complete: ${data.processed} records processed.`);
        fetchFgInventory();
        if (view === 'summary') fetchSummary();
      } else {
        toast.error('Import completed with errors — see downloaded receipt for details.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
      setImportResult({ errors: [e.response?.data?.detail || 'Import failed'], success: false });
    }
    setImporting(false);
  };

  return (
    <div className="p-6 md:p-8" data-testid="inventory-page">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Live Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Finished goods • {selectedBranchName || 'Select branch'}
            {view === 'detailed' ? ` • ${fgTotal} item${fgTotal === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Branch filter */}
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <Select
              value={fgFilters.branch_id || '_all'}
              onValueChange={(v) => setFgFilters((f) => ({ ...f, branch_id: v === '_all' ? '' : v }))}
            >
              <SelectTrigger className="w-[200px]" data-testid="fg-branch-filter">
                <SelectValue placeholder="Select branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all" data-testid="fg-branch-all">All Branches</SelectItem>
                {filtersData.branches.map((b) => (
                  <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PermButton
            entity="DamageLog"
            action="CREATE"
            variant="destructive"
            onClick={() => setDamageDialogOpen(true)}
            className="uppercase text-xs tracking-wide"
            data-testid="fg-log-damage-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Log Damage
          </PermButton>
        </div>
      </div>

      <LogDamageDialog
        open={damageDialogOpen}
        onOpenChange={setDamageDialogOpen}
        itemType="FG"
        branches={filtersData.branches}
        defaultBranch={selectedBranchName || ''}
        onLogged={() => { fetchFgInventory(); if (view === 'summary') fetchSummary(); }}
      />

      {/* Search + Filters toggle (shared) */}
      <div className="mb-4 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              placeholder="Search Buyer SKU ID..."
              value={fgFilters.search}
              onChange={(e) => setFgFilters((f) => ({ ...f, search: e.target.value }))}
              className="pl-10 font-mono"
              data-testid="fg-search-input"
            />
          </div>
          <Button
            variant={showFilters ? 'default' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            className="uppercase text-xs"
            data-testid="fg-filters-toggle"
          >
            <Filter className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" onClick={clearAdvancedFilters} className="text-xs" data-testid="fg-filters-clear">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="fg-filters-panel">
            <div>
              <label className="text-xs font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1 block">Vertical</label>
              <Select
                value={fgFilters.vertical_id || 'all'}
                onValueChange={(v) => setFgFilters((f) => ({ ...f, vertical_id: v === 'all' ? '' : v, model_id: '' }))}
              >
                <SelectTrigger data-testid="fg-vertical-filter">
                  <SelectValue placeholder="All Verticals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Verticals</SelectItem>
                  {(filtersData.verticals || []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}{v.code ? ` (${v.code})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1 block">Model</label>
              <Select
                value={fgFilters.model_id || 'all'}
                onValueChange={(v) => setFgFilters((f) => ({ ...f, model_id: v === 'all' ? '' : v }))}
              >
                <SelectTrigger data-testid="fg-model-filter">
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {filteredModels.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1 block">Brand</label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" type="button" className="w-full justify-between font-normal" data-testid="fg-brand-filter">
                    <span className="truncate">{brandFilterLabel}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[240px]">
                  <DropdownMenuItem
                    onSelect={() => setFgFilters((f) => ({ ...f, brand_ids: [] }))}
                    data-testid="fg-brand-filter-all"
                  >
                    All Brands
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {(filtersData.brands || []).map((b) => (
                    <DropdownMenuCheckboxItem
                      key={b.id}
                      checked={fgFilters.brand_ids.includes(b.id)}
                      onCheckedChange={() => toggleBrand(b.id)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`fg-brand-option-${b.id}`}
                    >
                      {b.name}{b.code ? ` (${b.code})` : ''}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>

      {/* View toggle */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-1" role="tablist" data-testid="inventory-view-toggle">
          <button
            type="button"
            onClick={() => setView('summary')}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${
              view === 'summary' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'
            }`}
            data-testid="inventory-view-summary"
          >
            <LayoutGrid className="inline w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Summary
          </button>
          <button
            type="button"
            onClick={() => setView('detailed')}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${
              view === 'detailed' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'
            }`}
            data-testid="inventory-view-detailed"
          >
            <TableIcon className="inline w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            SKU Detailed View
          </button>
        </div>

        {view === 'summary' ? (
          <Button
            variant="secondary"
            onClick={handleExportSummary}
            className="uppercase text-xs tracking-wide"
            data-testid="fg-summary-export-btn"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Download summary
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleExportDetailed}
              className="uppercase text-xs tracking-wide"
              data-testid="fg-export-btn"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Export
            </Button>
            <AgingReportPanel kind="fg" params={fgAgingParams} branchLabel={selectedBranchName} />
          </div>
        )}
      </div>

      {view === 'summary' ? (
        <SummaryView
          summary={summary}
          loading={summaryLoading}
          selectedBranchName={selectedBranchName}
        />
      ) : (
        <DetailedView
          fgInventory={fgInventory}
          fgTotal={fgTotal}
          filteredTotals={fgFilteredTotals}
          totalsBranch={fgFilters.branch_id ? selectedBranchName : ''}
          loading={loading}
          page={page}
          totalPages={totalPages}
          startIdx={startIdx}
          endIdx={endIdx}
          setPage={setPage}
        />
      )}

      {/* Import Dialog (preserved) */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Import Finished Goods Inventory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Import Mode</label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex flex-col">
                      <span className="font-medium">Add to Existing</span>
                      <span className="text-xs text-gray-500">Quantities will be added to current stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex flex-col">
                      <span className="font-medium">Replace Stock</span>
                      <span className="text-xs text-gray-500">Uploaded quantities will replace current stock</span>
                      <span className="text-[10px] text-amber-700 font-medium">⚠ Pre-import snapshot auto-downloaded</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Select Excel File</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="hidden"
                  id="import-file"
                  data-testid="import-file-input"
                />
                <label htmlFor="import-file" className="cursor-pointer">
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span>{importFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2" />
                      <p>Click to select file</p>
                      <p className="text-xs">.xlsx or .xls</p>
                    </div>
                  )}
                </label>
              </div>
              <Button variant="link" size="sm" onClick={downloadTemplate} className="text-xs">
                Download template with reference data
              </Button>
            </div>

            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-start gap-2">
                  {importResult.success ? <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" /> : <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />}
                  <div className="flex-1">
                    <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {importResult.success ? 'Import Successful' : 'Import Failed'}
                    </p>
                    {importResult.processed > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        Processed: {importResult.processed} | Added: {importResult.added} | Updated: {importResult.updated}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!importFile || importing} data-testid="confirm-import-btn">
              {importing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>) : (<><Upload className="h-4 w-4 mr-2" /> Import</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====================== Summary View ======================

function SummaryView({ summary, loading, selectedBranchName }) {
  if (loading && !summary) {
    return (
      <div className="p-16 text-center text-zinc-500 font-mono text-sm" data-testid="fg-summary-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" /> Loading summary...
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="p-16 text-center text-zinc-500 font-mono text-sm" data-testid="fg-summary-empty">
        No inventory summary available.
      </div>
    );
  }

  const { kpis, by_vertical, by_brand, matrix_brand_vertical, matrix_brand_model, top_combinations, top_skus } = summary;

  return (
    <div className="space-y-6" data-testid="fg-summary-view">
      {/* A. KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="fg-summary-kpis">
        <KpiTile label="Total units" value={fmtInt(kpis.total_units)} sub={selectedBranchName} testid="kpi-total-units" />
        <KpiTile label="Inventory value" value={fmtINRCompact(kpis.inventory_value)} sub="Accounting basis" testid="kpi-inventory-value" />
        <KpiTile label="Active SKUs" value={fmtInt(kpis.active_skus)} sub="Distinct buyer SKUs" testid="kpi-active-skus" />
        <KpiTile label="Unpriced SKUs" value={fmtInt(kpis.unpriced_skus)} sub="Qty > 0, value = 0" tone="amber" testid="kpi-unpriced-skus" />
      </div>

      {/* B. Donuts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DonutCard title="Quantity by Vertical" data={by_vertical} keyField="vertical" testid="donut-vertical" />
        <DonutCard title="Quantity by Brand" data={by_brand} keyField="brand" testid="donut-brand" />
      </div>

      {/* C. Pivot Brand × Vertical */}
      <PivotMatrixCard
        title="Brand × Vertical — Quantity"
        matrix={matrix_brand_vertical}
        rowHeader="Brand"
        testid="pivot-brand-vertical"
      />

      {/* D. Pivot Brand × Model */}
      <PivotMatrixCard
        title="Brand × Model — Quantity"
        matrix={matrix_brand_model}
        rowHeader="Brand"
        testid="pivot-brand-model"
      />

      {/* E. Top Combinations */}
      <RankedListCard
        title="Top Combinations"
        rows={top_combinations}
        columns={[
          { key: 'vertical', label: 'Vertical' },
          { key: 'brand', label: 'Brand' },
          { key: 'model', label: 'Model' },
        ]}
        testid="top-combinations"
      />

      {/* F. Top SKUs */}
      <RankedListCard
        title="Top SKUs"
        rows={top_skus}
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'model', label: 'Model' },
          { key: 'sku_id', label: 'SKU ID', mono: true },
        ]}
        testid="top-skus"
      />
    </div>
  );
}

function KpiTile({ label, value, sub, tone, testid }) {
  const valueClass = tone === 'amber' ? 'text-amber-600' : 'text-zinc-900';
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-2 text-3xl font-bold tabular-nums ${valueClass}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500 font-mono truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function DonutCard({ title, data, keyField, testid }) {
  const total = (data || []).reduce((acc, d) => acc + Number(d.qty || 0), 0);
  const chartData = (data || []).map((d, idx) => ({
    name: d[keyField],
    value: Number(d.qty || 0),
    color: DONUT_COLOURS[idx % DONUT_COLOURS.length],
  }));
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{title}</div>
      {total === 0 ? (
        <div className="h-[260px] flex items-center justify-center text-zinc-400 font-mono text-sm">
          No data
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="w-full md:w-1/2 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={1}>
                  {chartData.map((entry, idx) => (
                    <Cell key={`c-${idx}`} fill={entry.color} stroke="#fff" strokeWidth={1} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(value) => fmtInt(value)}
                  contentStyle={{ fontFamily: 'monospace', fontSize: 12, borderRadius: 2, border: '1px solid #e4e4e7' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="w-full md:w-1/2 space-y-1.5" data-testid={`${testid}-legend`}>
            {chartData.map((d, idx) => {
              const pct = total > 0 ? (d.value / total) * 100 : 0;
              return (
                <li key={`l-${idx}`} className="flex items-center justify-between text-xs font-mono">
                  <div className="flex items-center gap-2 truncate">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="truncate" title={d.name}>{d.name}</span>
                  </div>
                  <div className="text-zinc-700 tabular-nums">
                    {fmtInt(d.value)} <span className="text-zinc-400">({pct.toFixed(1)}%)</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PivotMatrixCard({ title, matrix, rowHeader, testid }) {
  const columns = matrix?.columns || [];
  const rows = matrix?.rows || [];
  const colTotals = matrix?.column_totals || [];
  const grandTotal = matrix?.grand_total || 0;
  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-700">{title}</h2>
        <span className="text-xs font-mono text-zinc-500">Grand total: <span className="font-bold text-zinc-700 tabular-nums">{fmtInt(grandTotal)}</span></span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">{rowHeader}</th>
              {columns.map((c) => (
                <th key={c} className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider whitespace-nowrap">{c}</th>
              ))}
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-bold text-zinc-700 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="p-8 text-center text-zinc-400 font-mono text-sm">No data</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.label} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="p-3 align-middle text-sm text-zinc-700 font-medium whitespace-nowrap">{row.label}</td>
                  {row.cells.map((cell, ci) => (
                    <td key={ci} className={`p-3 align-middle font-mono text-right text-sm ${cell == null ? 'text-zinc-300' : 'text-zinc-700'}`}>
                      {cell == null ? '–' : fmtInt(cell)}
                    </td>
                  ))}
                  <td className="p-3 align-middle font-mono text-right text-sm font-bold text-zinc-800">{fmtInt(row.total)}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-zinc-50 border-t-2 border-zinc-200">
              <tr>
                <td className="p-3 align-middle text-sm font-bold text-zinc-700 uppercase tracking-wider font-mono">Total</td>
                {colTotals.map((t, ci) => (
                  <td key={ci} className="p-3 align-middle font-mono text-right text-sm font-bold text-zinc-800">{fmtInt(t)}</td>
                ))}
                <td className="p-3 align-middle font-mono text-right text-sm font-bold text-zinc-900">{fmtInt(grandTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function RankedListCard({ title, rows, columns, testid }) {
  const totalQty = (rows || []).reduce((acc, r) => acc + Number(r.qty || 0), 0);
  const totalAmt = (rows || []).reduce((acc, r) => acc + Number(r.amount || 0), 0);
  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-200">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-700">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider w-12">#</th>
              {columns.map((c) => (
                <th key={c.key} className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">{c.label}</th>
              ))}
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Qty</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) ? (
              <tr>
                <td colSpan={columns.length + 3} className="p-8 text-center text-zinc-400 font-mono text-sm">No data</td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={`r-${idx}`} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="p-3 align-middle font-mono text-sm text-zinc-500 tabular-nums">{idx + 1}</td>
                  {columns.map((c) => (
                    <td key={c.key} className={`p-3 align-middle text-sm text-zinc-700 ${c.mono ? 'font-mono font-bold' : ''}`}>
                      {row[c.key]}
                    </td>
                  ))}
                  <td className="p-3 align-middle font-mono text-right text-sm font-medium text-zinc-800">{fmtInt(row.qty)}</td>
                  <td className={`p-3 align-middle font-mono text-right text-sm ${row.amount > 0 ? 'text-zinc-700' : 'text-zinc-300'}`}>
                    {row.amount > 0 ? fmtINR(row.amount) : '–'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows && rows.length > 0 && (
            <tfoot className="bg-zinc-50 border-t-2 border-zinc-200">
              <tr>
                <td className="p-3 align-middle text-sm font-bold text-zinc-700 uppercase tracking-wider font-mono" colSpan={columns.length + 1}>Grand total</td>
                <td className="p-3 align-middle font-mono text-right text-sm font-bold text-zinc-900">{fmtInt(totalQty)}</td>
                <td className="p-3 align-middle font-mono text-right text-sm font-bold text-zinc-900">{fmtINR(totalAmt)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ====================== Detailed View ======================

function DetailedView({ fgInventory, fgTotal, filteredTotals, totalsBranch, loading, page, totalPages, startIdx, endIdx, setPage }) {
  return (
    <div className="border border-border bg-white rounded-sm overflow-hidden" data-testid="fg-detailed-view">
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="fg-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Buyer SKU ID</th>
              <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Name</th>
              <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Branch</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider" title="Pre-draft physical view = On Hand + Dispatch Pending.">Total Stock</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-amber-600 uppercase tracking-wider" title="Quantity on open dispatch invoices.">Dispatch Pending</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-blue-600 uppercase tracking-wider" title="Incoming on Inter-Branch Transfers where this branch is the destination.">In-Transit</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-emerald-700 uppercase tracking-wider" title="Dispatchable stock = current_stock − Dispatch Pending − In-Transit. Same formula on vendor and production branches; pending-invoice stock is dispatchable.">On Hand</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-rose-600 uppercase tracking-wider" title="Pending Invoice — vendor-produced FG awaiting invoice.">Pending Invoice</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider" title="MWAC-blended unit cost.">Unit Cost</th>
              <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-700 uppercase tracking-wider" title="Accounting Value = Owned × Unit Cost.">Accounting Value</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="p-8 text-center text-zinc-500 font-mono text-sm">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading...
                </td>
              </tr>
            ) : fgInventory.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-12 text-center text-zinc-500 font-mono text-sm">
                  No inventory records match the current filters.
                </td>
              </tr>
            ) : (
              fgInventory.map((item) => {
                const dispatchPending = Number(item.dispatch_pending ?? item.allocated ?? 0);
                const onHand = Number(item.on_hand ?? item.current_stock ?? 0);
                const inTransit = Number(item.in_transit ?? 0);
                const total = Number(item.total ?? (onHand + dispatchPending));
                // Feb 2026: vendor-invoicing fields come back as `null` for
                // PRODUCTION (Bidso-owned) branches. Render `—` for them
                // Phase 5C-2 (Feb 2026): OWNED (INVOICED) column removed
                // from the grid (still in the Excel export). Pending
                // invoice still clamps at zero on VENDOR_SITE rows.
                const isVendorRow = item.invoiced_qty !== null && item.invoiced_qty !== undefined;
                const pendingInvoiceRaw = item.pending_invoice == null ? null : Number(item.pending_invoice);
                const pendingInvoice = pendingInvoiceRaw == null ? null : Math.max(pendingInvoiceRaw, 0);
                const overDispatched = Boolean(item.over_dispatched) && isVendorRow;
                const unitCost = item.unit_cost == null ? null : Number(item.unit_cost);
                const accountingValueRaw = item.accounting_value;
                const accountingValue = accountingValueRaw == null ? null : Number(accountingValueRaw);
                return (
                  <tr
                    key={`${item.buyer_sku_id}-${item.branch}`}
                    className="border-b border-zinc-100 hover:bg-zinc-50/50"
                    data-testid={`fg-row-${item.buyer_sku_id}`}
                  >
                    <td className="p-4 align-middle font-mono text-sm font-bold text-zinc-700">{item.buyer_sku_id}</td>
                    <td className="p-4 align-middle text-sm text-zinc-700 max-w-[400px]">
                      <span className="truncate block" title={item.sku_name || ''}>{item.sku_name || '-'}</span>
                    </td>
                    <td className="p-4 align-middle text-sm text-zinc-700">{item.branch}</td>
                    <td className="p-4 align-middle font-mono text-right font-medium text-zinc-700" data-testid={`fg-total-${item.buyer_sku_id}`}>
                      {total.toLocaleString()}
                    </td>
                    <td
                      className={`p-4 align-middle font-mono text-right ${dispatchPending > 0 ? 'text-amber-600 font-semibold' : 'text-zinc-400'}`}
                      data-testid={`fg-dispatch-pending-${item.buyer_sku_id}`}
                    >
                      {dispatchPending.toLocaleString()}
                    </td>
                    <td
                      className={`p-4 align-middle font-mono text-right ${inTransit > 0 ? 'text-blue-600 font-semibold' : 'text-zinc-400'}`}
                      data-testid={`fg-in-transit-${item.buyer_sku_id}`}
                    >
                      {inTransit.toLocaleString()}
                    </td>
                    <td
                      className={`p-4 align-middle font-mono text-right font-semibold ${onHand <= 0 ? 'text-red-600' : 'text-emerald-700'}`}
                      data-testid={`fg-on-hand-${item.buyer_sku_id}`}
                    >
                      {onHand.toLocaleString()}
                    </td>
                    <td
                      className={`p-4 align-middle font-mono text-right ${pendingInvoice > 0 ? 'text-rose-600 font-semibold' : 'text-zinc-400'}`}
                      data-testid={`fg-pending-invoice-${item.buyer_sku_id}`}
                    >
                      {pendingInvoice == null ? (
                        <span className="text-zinc-300">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {pendingInvoice.toLocaleString()}
                          {overDispatched && (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-amber-500"
                              title="Dispatched qty exceeds invoiced qty — reconcile vendor invoice"
                              data-testid={`fg-over-dispatched-${item.buyer_sku_id}`}
                            />
                          )}
                        </span>
                      )}
                    </td>
                    <td className="p-4 align-middle font-mono text-right text-zinc-700" data-testid={`fg-unit-cost-${item.buyer_sku_id}`}>
                      {unitCost == null ? <span className="text-zinc-300">—</span> : `₹ ${unitCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                    </td>
                    <td className="p-4 align-middle font-mono text-right font-semibold text-zinc-800" data-testid={`fg-accounting-value-${item.buyer_sku_id}`}>
                      {accountingValue == null ? <span className="text-zinc-300">—</span> : `₹ ${accountingValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {!loading && filteredTotals && (
            <tfoot className="bg-zinc-100 border-t-2 border-zinc-300" data-testid="fg-filtered-totals">
              <tr data-testid="fg-filtered-totals-row">
                <td className="p-4 align-middle font-mono text-sm" data-testid="fg-totals-buyer-sku"></td>
                <td className="p-4 align-middle text-sm font-bold uppercase tracking-wider text-zinc-800" data-testid="fg-totals-name">Total</td>
                <td className="p-4 align-middle text-sm font-semibold text-zinc-700" data-testid="fg-totals-branch">{totalsBranch}</td>
                <td className="p-4 align-middle font-mono text-right font-bold text-zinc-900" data-testid="fg-totals-total-stock">
                  {Number(filteredTotals.total_stock || 0).toLocaleString()}
                </td>
                <td className="p-4 align-middle font-mono text-right font-bold text-amber-700" data-testid="fg-totals-dispatch-pending">
                  {Number(filteredTotals.dispatch_pending || 0).toLocaleString()}
                </td>
                <td className="p-4 align-middle font-mono text-right font-bold text-blue-700" data-testid="fg-totals-in-transit">
                  {Number(filteredTotals.in_transit || 0).toLocaleString()}
                </td>
                <td className="p-4 align-middle font-mono text-right font-bold text-emerald-800" data-testid="fg-totals-on-hand">
                  {Number(filteredTotals.on_hand || 0).toLocaleString()}
                </td>
                <td className="p-4 align-middle font-mono text-right font-bold text-rose-700" data-testid="fg-totals-pending-invoice">
                  {Number(filteredTotals.pending_invoice || 0).toLocaleString()}
                </td>
                <td className="p-4 align-middle font-mono text-right" data-testid="fg-totals-unit-cost"></td>
                <td className="p-4 align-middle font-mono text-right font-bold text-zinc-900" data-testid="fg-totals-accounting-value">
                  ₹ {Number(filteredTotals.accounting_value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {fgTotal > 0 && (
        <div className="p-4 border-t border-zinc-200 flex items-center justify-between" data-testid="fg-pagination">
          <p className="text-sm text-zinc-500 font-mono">Showing {startIdx}–{endIdx} of {fgTotal}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} data-testid="fg-page-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-mono tabular-nums px-2">
              Page <span className="font-medium">{page}</span> of {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} data-testid="fg-page-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
